import React, { useState } from 'react';
import { View, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import api from '../../../../utils/api';
import { useToast } from 'react-native-toast-notifications';


const GROUPS = [
  { value: 'street', label: 'Calle / privada' },
  { value: 'category', label: 'Categoría' },
  { value: 'area', label: 'Área' },
  { value: 'origin', label: 'Origen' },
  { value: 'month', label: 'Mes' },
];


export default function TicketsReport({ communityId, params }: any) {
  const [groupBy, setGroupBy] = useState('category');

  const reportQuery = useQuery<any>({
    queryKey: ['tickets.report', communityId, groupBy, params],
    queryFn: () => api
      .get(`/communities/${communityId}/tickets/report`, { params: { ...params, group_by: groupBy } })
      .then(r => r.data),
    enabled: !!communityId,
    placeholderData: keepPreviousData,
  });

  const rows: any[] = reportQuery.data?.rows ?? [];
  const totals = reportQuery.data?.totals;
  const groupLabel = GROUPS.find(g => g.value === groupBy)?.label;

  return (
    <View style={tw`flex-1`}>
      <View style={tw`flex-row items-center px-5 py-3 border-b border-gray-100 gap-3`}>
        <Text style={tw`text-xs text-gray-500`}>Agrupar por</Text>
        <View style={tw`flex-row gap-2`}>
          {GROUPS.map(g => {
            const active = g.value === groupBy;
            return (
              <TouchableOpacity
                key={g.value}
                onPress={() => setGroupBy(g.value)}
                style={tw.style(`px-3 py-1.5 rounded-full`, active ? 'bg-secondary' : 'bg-white border border-gray-200')}
              >
                <Text style={tw.style(`text-xs font-500`, active ? 'text-white' : 'text-gray-600')}>{g.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
        <View style={tw`flex-1`} />
        {reportQuery.isFetching && <ActivityIndicator size="small" color="#6366f1" />}
        <ExcelButton />
      </View>

      <View style={tw`flex-row px-5 py-3 border-b border-gray-200/70 bg-gray-50`}>
        <Text style={tw`flex-1 text-xs font-500 text-gray-500`}>{groupLabel}</Text>
        <HeaderCell>Total</HeaderCell>
        <HeaderCell>Nuevos</HeaderCell>
        <HeaderCell>En seguimiento</HeaderCell>
        <HeaderCell>Atendidos</HeaderCell>
        <HeaderCell>% atendidos</HeaderCell>
        <HeaderCell wide>Tiempo prom. de cierre</HeaderCell>
      </View>

      {reportQuery.isLoading ? (
        <View style={tw`px-5 py-8 items-center`}>
          <ActivityIndicator size="small" color="#6366f1" />
        </View>
      ) : rows.length === 0 ? (
        <View style={tw`py-12 items-center`}>
          <Ionicons name="bar-chart-outline" size={48} color="#d1d5db" />
          <Text style={tw`text-gray-400 mt-3`}>No hay reportes con estos filtros</Text>
        </View>
      ) : (
        <ScrollView style={tw`flex-1`}>
          {rows.map((row: any) => (
            <ReportRow key={row.key ?? '__none'} row={row} />
          ))}
          {totals && <ReportRow row={{ ...totals, label: 'Total' }} isTotal />}
        </ScrollView>
      )}
    </View>
  );
}

/** Exportación a Excel: el botón ya está, la descarga llega en la siguiente fase. */
export function ExcelButton() {
  const toast = useToast();
  return (
    <TouchableOpacity
      onPress={() => toast.show('La exportación a Excel estará disponible pronto')}
      style={tw`flex-row items-center border border-gray-200 rounded-full px-4 py-2`}
    >
      <Ionicons name="download-outline" size={14} color="#374151" />
      <Text style={tw`text-sm text-gray-700 ml-1.5`}>Exportar Excel</Text>
    </TouchableOpacity>
  );
}

function HeaderCell({ children, wide }: any) {
  return (
    <Text style={tw.style(`text-xs font-500 text-gray-500 text-right`, wide ? 'w-40' : 'w-28')}>{children}</Text>
  );
}

function ReportRow({ row, isTotal }: any) {
  const isEmptyGroup = !isTotal && (row.key === null || row.key === '');

  return (
    <View style={tw.style(
      `flex-row items-center px-5 py-3 border-b border-gray-200/70`,
      isTotal && 'bg-gray-50 border-t border-gray-200',
    )}>
      <View style={tw`flex-1 flex-row items-center pr-3`}>
        {row.color && <View style={tw.style(`w-2.5 h-2.5 rounded-full mr-2`, { backgroundColor: row.color })} />}
        <Text
          style={tw.style(`text-sm`, isTotal ? 'font-600 text-gray-900' : isEmptyGroup ? 'text-gray-500 italic' : 'text-gray-800')}
          numberOfLines={1}
        >
          {row.label}
        </Text>
      </View>
      <Cell bold={isTotal}>{row.total}</Cell>
      <Cell bold={isTotal} muted={!row.nuevo}>{row.nuevo}</Cell>
      <Cell bold={isTotal} muted={!row.en_seguimiento}>{row.en_seguimiento}</Cell>
      <Cell bold={isTotal} muted={!row.atendida}>{row.atendida}</Cell>
      <Cell bold={isTotal}>{`${row.attended_pct}%`}</Cell>
      <Cell bold={isTotal} wide muted={row.avg_close_hours === null}>{formatHours(row.avg_close_hours)}</Cell>
    </View>
  );
}

function Cell({ children, wide, bold, muted }: any) {
  return (
    <Text style={tw.style(
      `text-sm text-right`,
      wide ? 'w-40' : 'w-28',
      bold ? 'font-600 text-gray-900' : muted ? 'text-gray-300' : 'text-gray-700',
    )}>
      {children}
    </Text>
  );
}

function formatHours(hours: number | null) {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1).replace(/\.0$/, '')} h`;
  return `${(hours / 24).toFixed(1).replace(/\.0$/, '')} días`;
}
